import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import Cards from '@cloudscape-design/components/cards';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { Spinner } from '@cloudscape-design/components';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PDFRedactionDetector = forwardRef((props, ref) => {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Expose reset function to parent component
  useImperativeHandle(ref, () => ({
    resetAnalysis: () => {
      setFile(null);
      setResults(null);
      setLoading(false);
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
  }));

  // Convert PDF to images for analysis
  const convertPdfToImages = async (pdfFile) => {
    const arrayBuffer = await pdfFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    const images = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.5 });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      images.push(canvas.toDataURL());
    }

    return images;
  };

  // Helper function to check if a block is surrounded by text
  // This helps identify white-out areas (which are typically surrounded by text)
  const isBlockSurroundedByText = (bx, by, blocksX, blocksY, data, canvasWidth, blockSize) => {
    // Check surrounding blocks for text-like patterns
    let textBlocksAround = 0;

    // Check the 8 surrounding blocks
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue; // Skip the center block

        const checkX = bx + dx;
        const checkY = by + dy;

        // Skip if out of bounds
        if (checkX < 0 || checkY < 0 || checkX >= blocksX || checkY >= blocksY) continue;

        // Check if this block has text-like patterns
        if (hasTextPattern(checkX, checkY, data, canvasWidth, blockSize)) {
          textBlocksAround++;
        }
      }
    }

    // If at least 3 surrounding blocks have text, this might be a white-out area
    return textBlocksAround >= 3;
  };

  // Helper function to detect text-like patterns in a block
  const hasTextPattern = (bx, by, data, canvasWidth, blockSize) => {
    let blackPixels = 0;
    let totalPixels = 0;

    for (let y = by * blockSize; y < (by + 1) * blockSize; y++) {
      for (let x = bx * blockSize; x < (bx + 1) * blockSize; x++) {
        const idx = (y * canvasWidth + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];

        // Count dark pixels that might be text
        if (r < 100 && g < 100 && b < 100) blackPixels++;
        totalPixels++;
      }
    }

    // Text typically has a certain ratio of dark to light pixels
    const blackRatio = blackPixels / totalPixels;
    return blackRatio > 0.05 && blackRatio < 0.4;
  };

  // Analyze a rendered PDF page for redactions
  const analyzePageForRedactions = (imageData) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Counters for detection
        let blackRectCount = 0;
        let suspiciousAreaCount = 0;

        // Scan for black rectangles (potential redactions)
        // We'll look for clusters of black pixels that form rectangles
        const blackPixels = [];
        const blackThreshold = 50; // RGB values below this are considered "black"

        // First pass: identify black pixels
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // Check if pixel is black or very dark
          if (r < blackThreshold && g < blackThreshold && b < blackThreshold) {
            const pixelIndex = i / 4;
            const x = pixelIndex % canvas.width;
            const y = Math.floor(pixelIndex / canvas.width);
            blackPixels.push({ x, y });
          }
        }

        // If we have enough black pixels, look for rectangles
        if (blackPixels.length > 100) {
          // Improved rectangle detection - optimized for form-style redactions
          // This approach is better at detecting the rectangular redactions commonly used in forms

          // Group black pixels by rows to find horizontal lines of black pixels
          const rowGroups = {};
          blackPixels.forEach((pixel) => {
            if (!rowGroups[pixel.y]) {
              rowGroups[pixel.y] = [];
            }
            rowGroups[pixel.y].push(pixel.x);
          });

          // Sort each row's pixels
          Object.keys(rowGroups).forEach((y) => {
            rowGroups[y].sort((a, b) => a - b);
          });

          // Find continuous horizontal segments in each row
          const horizontalSegments = [];
          Object.keys(rowGroups).forEach((y) => {
            const row = rowGroups[y];
            let segmentStart = row[0];
            let currentX = row[0];

            for (let i = 1; i < row.length; i++) {
              // If there's a gap larger than 3 pixels, consider it a new segment
              if (row[i] - currentX > 3) {
                // Only add segments that are wide enough (at least 20 pixels)
                if (currentX - segmentStart >= 20) {
                  horizontalSegments.push({
                    y: parseInt(y),
                    startX: segmentStart,
                    endX: currentX,
                    width: currentX - segmentStart + 1,
                  });
                }
                segmentStart = row[i];
              }
              currentX = row[i];
            }

            // Add the last segment if it's wide enough
            if (currentX - segmentStart >= 20) {
              horizontalSegments.push({
                y: parseInt(y),
                startX: segmentStart,
                endX: currentX,
                width: currentX - segmentStart + 1,
              });
            }
          });

          // Group horizontal segments that are close vertically and have similar horizontal positions
          // This helps identify rectangular blocks
          const potentialRectangles = [];
          let currentRect = null;

          // Sort segments by y-coordinate
          horizontalSegments.sort((a, b) => a.y - b.y);

          for (let i = 0; i < horizontalSegments.length; i++) {
            const segment = horizontalSegments[i];

            // If we don't have a current rectangle or this segment is far from the current one,
            // start a new rectangle
            if (!currentRect || segment.y - currentRect.endY > 5) {
              if (currentRect && currentRect.height >= 5) {
                potentialRectangles.push(currentRect);
              }

              currentRect = {
                startX: segment.startX,
                endX: segment.endX,
                startY: segment.y,
                endY: segment.y,
                width: segment.width,
                height: 1,
              };
            }
            // If this segment is close to the current rectangle vertically and has similar horizontal position,
            // extend the current rectangle
            else if (
              Math.abs(segment.startX - currentRect.startX) < 20 &&
              Math.abs(segment.endX - currentRect.endX) < 20
            ) {
              currentRect.endY = segment.y;
              currentRect.height = currentRect.endY - currentRect.startY + 1;
              // Update width to the average
              currentRect.startX = Math.min(currentRect.startX, segment.startX);
              currentRect.endX = Math.max(currentRect.endX, segment.endX);
              currentRect.width = currentRect.endX - currentRect.startX + 1;
            }
          }

          // Add the last rectangle if it's tall enough
          if (currentRect && currentRect.height >= 5) {
            potentialRectangles.push(currentRect);
          }

          // Filter rectangles by minimum size
          const significantRects = potentialRectangles.filter(
            (rect) => rect.width >= 30 && rect.height >= 5 && rect.width * rect.height >= 300,
          );

          blackRectCount = significantRects.length;

          // Calculate the total area of detected rectangles
          const totalArea = significantRects.reduce((sum, rect) => sum + rect.width * rect.height, 0);

          // Calculate confidence based on number and size of detected rectangles
          // For form-style redactions, we can be more confident
          const confidence = Math.min(
            0.98,
            blackRectCount > 0
              ? 0.7 + Math.min(0.28, blackRectCount * 0.05 + (totalArea / (canvas.width * canvas.height)) * 3)
              : 0,
          );

          // If we found significant rectangles, consider it redacted
          if (blackRectCount > 0) {
            canvas.remove();
            resolve({
              redacted: true,
              suspicious: false,
              confidence,
              method: {
                method: 'Black rectangular redactions',
                area: `${blackRectCount} rectangle(s) detected`,
                rectangles: blackRectCount,
              },
            });
            return;
          }
        }

        // Look for suspicious patterns - areas with unusual pixel distribution
        // This could indicate white-out areas or other redaction techniques
        const blockSize = 20;
        const blocksX = Math.floor(canvas.width / blockSize);
        const blocksY = Math.floor(canvas.height / blockSize);

        for (let by = 0; by < blocksY; by++) {
          for (let bx = 0; bx < blocksX; bx++) {
            let whiteCount = 0;
            let blackCount = 0;
            let totalPixels = 0;

            // Analyze each block for suspicious patterns
            for (let y = by * blockSize; y < (by + 1) * blockSize && y < canvas.height; y++) {
              for (let x = bx * blockSize; x < (bx + 1) * blockSize && x < canvas.width; x++) {
                const idx = (y * canvas.width + x) * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];

                // Count white and black pixels
                if (r > 240 && g > 240 && b > 240) whiteCount++;
                if (r < 30 && g < 30 && b < 30) blackCount++;
                totalPixels++;
              }
            }

            // Check for suspicious patterns
            const whiteRatio = whiteCount / totalPixels;
            const blackRatio = blackCount / totalPixels;

            // Blocks with unusual white/black ratios might be redactions
            if (
              (whiteRatio > 0.95 && isBlockSurroundedByText(bx, by, blocksX, blocksY, data, canvas.width, blockSize)) ||
              (blackRatio > 0.8 && blackRatio < 0.98)
            ) {
              suspiciousAreaCount++;
            }
          }
        }

        // Calculate confidence for suspicious areas
        const suspiciousConfidence = Math.min(0.7, 0.3 + suspiciousAreaCount * 0.02);

        canvas.remove();

        if (suspiciousAreaCount > 2) {
          resolve({
            redacted: false,
            suspicious: true,
            confidence: suspiciousConfidence,
          });
        } else {
          resolve({
            redacted: false,
            suspicious: false,
            confidence: 0,
          });
        }
      };

      img.src = imageData;
    });
  };

  // Main detection function
  const detectRedactions = async (pdfFile) => {
    try {
      // Use the existing convertPdfToImages function to render PDF pages
      const pageImages = await convertPdfToImages(pdfFile);

      const results = {
        totalPages: pageImages.length,
        redactedPages: [],
        suspiciousPages: [],
        confidence: {},
        detectionMethods: [],
      };

      // Analyze each page for redactions
      for (let pageNum = 0; pageNum < pageImages.length; pageNum++) {
        const analysis = await analyzePageForRedactions(pageImages[pageNum]);

        if (analysis.redacted) {
          results.redactedPages.push(pageNum + 1);
          results.confidence[pageNum + 1] = analysis.confidence;
          results.detectionMethods.push({
            page: pageNum + 1,
            ...analysis.method,
          });
        } else if (analysis.suspicious) {
          results.suspiciousPages.push(pageNum + 1);
          results.confidence[pageNum + 1] = analysis.confidence;
        }
      }

      return results;
    } catch (error) {
      console.error('Error processing PDF:', error);
      throw new Error('Failed to process PDF file');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError('');
      setResults(null);
    } else {
      setError('Please select a valid PDF file');
      setFile(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const detectionResults = await detectRedactions(file);
      setResults(detectionResults);
    } catch (err) {
      setError('Error analyzing PDF: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setError('');
      setResults(null);
    } else {
      setError('Please drop a valid PDF file');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const resetAnalysis = () => {
    setFile(null);
    setResults(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Container>
      <SpaceBetween size="l">
        {/* Header */}
        <Header
          variant="h1"
          description="Upload a PDF to analyze for potential redacted content using pattern recognition and visual analysis."
        >
          PDF Falsified Detector
        </Header>

        {/* File Upload Area */}
        <Container>
          <div
            style={{
              border: '2px dashed',
              borderColor: file ? '#037f0c' : '#adb5bd',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: file ? '#f0fff4' : '#f8f9fa',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => !file && fileInputRef.current?.click()}
          >
            {file ? (
              <SpaceBetween size="m" direction="vertical" alignItems="center">
                <Box textAlign="center">
                  <span role="img" aria-label="File" style={{ fontSize: '24px' }}>
                    📄
                  </span>
                </Box>
                <div>
                  <Box variant="h3" color="text-status-success">
                    {file.name}
                  </Box>
                  <Box variant="small" color="text-status-success">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Box>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetAnalysis();
                  }}
                  variant="icon"
                  iconName="close"
                />
              </SpaceBetween>
            ) : (
              <SpaceBetween size="m" direction="vertical" alignItems="center">
                <Box textAlign="center">
                  <span role="img" aria-label="Upload" style={{ fontSize: '32px' }}>
                    📤
                  </span>
                </Box>
                <Box variant="h3">Drop your PDF here or click to browse</Box>
                <Box variant="small" color="text-body-secondary">
                  Supports PDF files up to 50MB
                </Box>
              </SpaceBetween>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {error && (
            <Box padding={{ top: 'l' }}>
              <Alert type="error" header="Error">
                {error}
              </Alert>
            </Box>
          )}
        </Container>

        {/* Analyze Button */}
        {file && !results && (
          <Box textAlign="center">
            <Button onClick={handleAnalyze} disabled={loading} variant="primary" loading={loading}>
              {loading ? 'Analyzing...' : 'Analyze for Redactions'}
            </Button>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <SpaceBetween size="m" direction="vertical" alignItems="center">
            <Spinner size="large" />
            <Box variant="p" color="text-body-secondary">
              Scanning PDF pages for redacted content...
            </Box>
          </SpaceBetween>
        )}

        {/* Results */}
        {results && (
          <SpaceBetween size="l">
            {/* Summary */}
            <Container header={<Header variant="h2">Analysis Summary</Header>}>
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="h1" color="text-status-info" textAlign="center">
                    {results.totalPages}
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Total Pages
                  </Box>
                </div>
                <div>
                  <Box variant="h1" color="text-status-error" textAlign="center">
                    {results.redactedPages.length}
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Redacted Pages
                  </Box>
                </div>
                <div>
                  <Box variant="h1" color="text-status-warning" textAlign="center">
                    {results.suspiciousPages.length}
                  </Box>
                  <Box variant="small" color="text-body-secondary" textAlign="center">
                    Suspicious Pages
                  </Box>
                </div>
              </ColumnLayout>
            </Container>

            {/* Redacted Pages */}
            {results.redactedPages.length > 0 && (
              <Container
                header={
                  <Header
                    variant="h2"
                    description="Pages with detected redaction marks"
                    actions={
                      <StatusIndicator type="error">
                        {results.redactedPages.length} {results.redactedPages.length === 1 ? 'page' : 'pages'}
                      </StatusIndicator>
                    }
                  >
                    Detected Redactions
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {results.redactedPages.map((page) => {
                    const method = results.detectionMethods.find((m) => m.page === page);
                    const confidence = results.confidence[page] * 100;

                    return (
                      <Cards
                        key={page}
                        cardDefinition={{
                          header: (item) => <Box variant="h3">Page {item.page}</Box>,
                          sections: [
                            {
                              id: 'method',
                              header: 'Detection Method',
                              content: (item) => item.method,
                            },
                            {
                              id: 'coverage',
                              header: 'Coverage',
                              content: (item) => item.area,
                            },
                            {
                              id: 'confidence',
                              header: 'Confidence',
                              content: (item) => (
                                <SpaceBetween size="xs">
                                  <Box>{Math.round(confidence)}%</Box>
                                  <ProgressBar
                                    value={confidence}
                                    variant={confidence > 80 ? 'error' : 'warning'}
                                    label=""
                                  />
                                </SpaceBetween>
                              ),
                            },
                          ],
                        }}
                        cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
                        items={[
                          {
                            page,
                            ...method,
                          },
                        ]}
                      />
                    );
                  })}
                </SpaceBetween>
              </Container>
            )}

            {/* Suspicious Pages */}
            {results.suspiciousPages.length > 0 && (
              <Container
                header={
                  <Header
                    variant="h2"
                    description="Pages with suspicious content patterns"
                    actions={
                      <StatusIndicator type="warning">
                        {results.suspiciousPages.length} {results.suspiciousPages.length === 1 ? 'page' : 'pages'}
                      </StatusIndicator>
                    }
                  >
                    Suspicious Content
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {results.suspiciousPages.map((page) => {
                    const confidence = results.confidence[page] * 100;

                    return (
                      <Cards
                        key={page}
                        cardDefinition={{
                          header: (item) => <Box variant="h3">Page {item.page}</Box>,
                          sections: [
                            {
                              id: 'method',
                              header: 'Detection Method',
                              content: () => 'Suspicious pattern analysis',
                            },
                            {
                              id: 'confidence',
                              header: 'Confidence',
                              content: (item) => (
                                <SpaceBetween size="xs">
                                  <Box>{Math.round(confidence)}%</Box>
                                  <ProgressBar value={confidence} variant="warning" label="" />
                                </SpaceBetween>
                              ),
                            },
                          ],
                        }}
                        cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
                        items={[{ page }]}
                      />
                    );
                  })}
                </SpaceBetween>
              </Container>
            )}

            {/* Clean Pages */}
            {results.redactedPages.length === 0 && results.suspiciousPages.length === 0 && (
              <Container>
                <SpaceBetween size="m" direction="vertical" alignItems="center">
                  <Box textAlign="center">
                    <StatusIndicator type="success" iconAriaLabel="Success">
                      No Redactions Detected
                    </StatusIndicator>
                  </Box>
                  <Box variant="p" color="text-status-success" textAlign="center">
                    The PDF appears to be free of obvious redacted content based on our analysis.
                  </Box>
                </SpaceBetween>
              </Container>
            )}

            {/* New Analysis Button */}
            <Box textAlign="center">
              <Button onClick={resetAnalysis} variant="primary">
                Analyze Another PDF
              </Button>
            </Box>
          </SpaceBetween>
        )}

        {/* Technical Info */}
        <Container header={<Header variant="h3">Detection Methods</Header>}>
          <SpaceBetween size="xs">
            <Box variant="small">• Black rectangle detection (common redaction bars)</Box>
            <Box variant="small">• White-out area identification</Box>
            <Box variant="small">• Text removal pattern analysis</Box>
            <Box variant="small">• Image overlay detection</Box>
            <Box variant="small">• Pixel density anomalies</Box>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Container>
  );
});

export default PDFRedactionDetector;
